import axios from "axios";
import api from "../../api/services/axiosService";
import {SOLR_API, USER_MANAGEMENT_API_KEY, USER_MANAGEMENT_API_SECRET_KEY} from "../../envConstants";
import {errorMessage, successMessage} from "../../utils/helper";
import {trackApiAuthRejected} from "../../utils/zymedSessionDiagnostics";
// if Any changes required on these api please update the fucntionality in the service file

const BASE_URL = process.env.REACT_APP_USER_MANAGEMENT; // need to add this in env (this might change in future)
const SOLR_BASE_URL = SOLR_API; // need to add this in env (this might change in future)
const userPreferencesApiConfig: any = {
    isTShots: true,
};

export const getSpecialties = async () => {
    let res: any = {};
    try {
        res = await api.post(`${BASE_URL}/master/pm-specialities`, {});

        if (res?.status === 200) {
            res = res?.data;
        }
    } catch (e) {
        console.error("Error fetchSpecialities : ", e);
    }
    return res;
};

export const getMasterData = async (dataType: string[]) => {
    try {
        const response = await api.post(
            `${BASE_URL}/master/data`,
            {
                dataType,
            },
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching master data:", error);
        throw error;
    }
};

export const getSuggestions = async (query: string) => {
    try {
        const response = await fetch(
            `${SOLR_BASE_URL}/suggest?suggest.count=50&suggest.dictionary=ft2&suggest.q=${query}&suggest=true&wt=json`,
            {
                headers: {
                    authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
                    accept: "application/json",
                },
            }
        );
        return await response.json();
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        throw error;
    }
};

/*
 * Screens hold the token in the store, which is empty on every app open until
 * TatvaPediaHomeScreen fills it, while the saved token is already in localStorage. Sending
 * that empty value goes out as "Bearer null", which the backend rejects as "Invalid token".
 */
const usablePediaToken = (token: any): string | null => {
    const usable = token && token !== "null" && token !== "undefined";
    return usable ? token : localStorage.getItem("tatvaPediaToken");
};

/*
 * The token is still rejected after pediaToken's silent retry. The doctor is signed in to
 * the app, so don't wipe storage or send them to /on-boarding: that also throws away a
 * working session and mints another token, which registers another device session.
 * Returning undefined sends them back to the screen they came from.
 */
const reportContentRejected = (contentId: number, token: any, httpStatus: number, body: any) => {
    trackApiAuthRejected({tokenKind: "pedia", url: `${BASE_URL}/content-management/content-detail`, attempt: "final", token, httpStatus, body, contentId, whatHappened: "sent_back_with_message"});
    errorMessage("Couldn't open this content. Please try again.");
};

export const getContentDetail = async (contentInfoId: number, tokenFromScreen: any) => {
    const token = usablePediaToken(tokenFromScreen);
    try {
        const response = await axios.get(
            `${BASE_URL}/content-management/content-detail?contentInfoId=${contentInfoId}`,
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (response.data && response.data.status === 403) {
            reportContentRejected(contentInfoId, token, response.status, response.data);
            return undefined;
        }

        return response.data;
    } catch (error: any) {
        console.error("Error fetching content detail:", error);
        
        if (error.response && error.response.status === 403) {
            reportContentRejected(contentInfoId, token, 403, error.response.data);
            return undefined;
        }
        
        errorMessage("Error fetching content details");
        // throw error;
    }
};

export const likeContent = async (
    docId: number,
    docTypeId: number,
    isLike: boolean,
    token: any
) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/content/like`,
            {
                docId,
                docTypeId,
                isLike,
            },
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error liking content:", error);
        errorMessage(error);
        // throw error;
    }
};

export const bookmarkContent = async (
    docId: number,
    docTypeId: string,
    isBookmark: boolean,
    token: any
) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/content/bookmark`,
            {
                docId,
                docTypeId,
                isBookmark,
            },
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (response.data?.message) {
            if (isBookmark) {
                successMessage({message: "Content bookmarked successfully"});
            } else {
                successMessage({message: "Content removed from bookmarks"});
            }
        }
        return response.data;
    } catch (error) {
        console.error("Error bookmarking content:", error);
        errorMessage(error);
        // throw error;
    }
};

export const getContentUrl = async (
    id: number,
    contentTypeId: string,
    uploadContentUrl: string,
    token: any
) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/content-management/get-document-signed-url?contentTypeId=${contentTypeId}&contentGenreId=undefined&contentId=${id}&fileName=${uploadContentUrl}`,
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching content detail:", typeof error);
        errorMessage(error);
        // throw error;
    }
};

// showError=false for the background prefetch: no toast before the user has tapped play
export const getMediaData = async (mediaId: number, token: any, showError: boolean = true) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/media/stream?mediaId=${mediaId}`,
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching content detail:", typeof error);
        if (showError) {
            errorMessage(error);
        }
        // throw error;
    }
};

export const getReviews = async (
    docId: number,
    docTypeId: number,
    token: any
) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/content/fetch-reviews`,
            {
                docId,
                docTypeId,
            },
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching content detail:", error);
        errorMessage(error);
        // throw error;
    }
};

export const sendReview = async (
    docId: number,
    docTypeId: number,
    reviewText: string,
    token: any
) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/content/review`,
            {
                docId,
                docTypeId,
                reviewText,
            },
            {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching content detail:", error);
        errorMessage(error);
        // throw error;
    }
};

interface SolrSearchParams {
    selectedFilters: {
        "Content Type": Array<{ id: number; code: string; sequence: number }>;
        Genre: Array<{
            id: number;
            code: string;
            depends: string;
            pmEnabled: boolean;
        }>;
        Continuum: Array<{
            id: number;
            code: string;
            depends: string;
            pmEnabled: boolean;
        }>;
        Speciality: Array<{
            id: number;
            code: string;
            displayName: string;
            pmMasterId?: number;
            pmEnabled: boolean;
        }>;
    };
    page?: number;
    pageSize?: number;
}

export const searchTatvaPediaContent = async (params: SolrSearchParams) => {
    try {
        // Build filter query array
        const fq = [
            "status:489",
            "contentCountryId:(1)",
            `scheduled_published_date:[* TO ${new Date().toISOString()}]`,
        ];

        // Add filters from selected items
        if (params.selectedFilters["Content Type"]?.length > 0) {
            const contentTypeIds = params.selectedFilters["Content Type"].map(
                (item) => item.id
            );
            fq.push(`contentTypeId:(${contentTypeIds.join(" OR ")})`);
        }

        if (params.selectedFilters["Genre"]?.length > 0) {
            const genreIds = params.selectedFilters["Genre"].map((item) => item.id);
            fq.push(`contentGenreId:(${genreIds.join(" OR ")})`);
        }

        if (params.selectedFilters["Continuum"]?.length > 0) {
            const continuumIds = params.selectedFilters["Continuum"].map(
                (item) => item.id
            );
            fq.push(`contentContinuumId:(${continuumIds.join(" OR ")})`);
        }

        if (params.selectedFilters["Speciality"]?.length > 0) {
            const specialityIds = params.selectedFilters["Speciality"].map(
                (item) => item.id
            );
            fq.push(`speciality_id:(${specialityIds.join(" OR ")})`);
        }

        const response = await fetch(`${SOLR_BASE_URL}/select`, {
            method: "POST",
            headers: {
                authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
                accept: "application/json",
                "content-type": "application/json",
            },
            body: JSON.stringify({
                params: {
                    q: "*:*",
                    fq,
                    sort: "publishedDate desc",
                    start: params.page ? (params.page - 1) * (params.pageSize || 10) : 0,
                    rows: params.pageSize || 10,
                },
            }),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch content");
        }

        return await response.json();
    } catch (error) {
        console.error("Error searching content:", error);
        throw error;
    }
};

interface LoginResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
    id_token: string;
}

interface LoginParams {
    username: string;
    password: string;
}

export const TatvaPediaToken = async ({
                                          username,
                                          password,
                                      }: LoginParams): Promise<LoginResponse> => {
    try {
        const formData = new URLSearchParams({
            client_id: "client",
            username: username,
            password: password,
            grant_type: "custom_password",
            scope: "openid,profile,read",
        });

        const response = await axios({
            method: "post",
            url: `${process.env.REACT_APP_TATVA_PEDIA}/oauth2/token`,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: `Basic Y2xpZW50OnNlY3JldA==`,
            },
            data: formData,
        });

        if (!response.data) {
            throw new Error("Login failed");
        }

        return response.data;
    } catch (error) {
        console.error("Error during login:", error);
        throw error;
    }
};

interface SearchParams {
    query: string;
    contentType?: string | string[];
    page?: number;
    pageSize?: number;
}

export const searchContent = async ({
                                        query,
                                        contentType,
                                        page = 0,
                                        pageSize = 15,
                                    }: SearchParams) => {
    try {
        const currentDate = new Date().toISOString();

        // Build filter queries
        const fq = [
            "status:(489 OR 424 OR 495)",
            "contentCountryId:(1)",
            `scheduled_published_date:[* TO ${currentDate}]`,
        ];

        // Add content type filter if provided - handle both single and multiple types
        if (contentType) {
            if (Array.isArray(contentType)) {
                // Multiple content types - create OR condition
                const typeConditions = contentType
                    .map((type) => `"${type}"`)
                    .join(" OR ");
                fq.push(`consolidatedModuleId:(${typeConditions})`);
            } else {
                // Single content type
                fq.push(`consolidatedModuleId: ("${contentType}")`);
            }
        }

        // Build query parameters
        const params = new URLSearchParams({
            defType: "edismax",
            // facet: "on",
            // highlightMultiTerm: "true",
            // "hl.fl": "keywords",
            // "hl.simple.post": "</b>",
            // "hl.simple.pre": "<b>",
            // hl: "on",
            q: `exact_title:"${query}*"`,
            qf: "contentTitleSearch keywords",
            rows: pageSize.toString(),
            sort: "scheduled_published_date desc",
            start: (page * pageSize).toString(),
            // stopwords: "true",
        });

        // Add filter queries
        fq.forEach((filter) => params.append("fq", filter));

        const response = await fetch(
            `${SOLR_BASE_URL}/select?${params.toString()}`,
            {
                headers: {
                    authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
                    accept: "application/json",
                },
            }
        );

        if (!response.ok) {
            throw new Error("Failed to fetch search results");
        }

        return await response.json();
    } catch (error) {
        console.error("Error searching content:", error);
        throw error;
    }
};

export const getWatchedContent = async (token: string) => {
    try {
        const response = await axios.get(`${BASE_URL}/content-watched`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                country: "1",
                device_info: localStorage.getItem('device_info'),
                device_lat: "unknown",
                device_lng: "unknown",
                device_location: "unknown",
                device_type: localStorage.getItem('device_type'),
            },
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching watched content:", error);
        errorMessage(error);
    }
};

/*
 * Saved items are a side load on the Pedia screens. If the token is still rejected after
 * pediaToken's silent retry, show the screen without them. Wiping storage here would throw
 * away a working session and mint another token, which registers another device session.
 */
const reportMyListRejected = (type: string, token: string, httpStatus: number, body: any) =>
    trackApiAuthRejected({tokenKind: "pedia", url: `${BASE_URL}/user/my-list`, attempt: "final", token, httpStatus, body, listType: type, whatHappened: "screen_opened_without_saved_items"});

export const getLikeBookmarkedContent = async (type: string, tokenFromScreen: any) => {
    // No token yet: the screens load saved items again once the token arrives.
    const token = usablePediaToken(tokenFromScreen);
    if (!token) return undefined;

    try {
        const response = await axios.get(`${BASE_URL}/user/my-list`, {
            params: {
                type: type,
            },
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                country: "1",
                device_info: localStorage.getItem("device_info"),
                device_lat: "unknown",
                device_lng: "unknown",
                device_location: "unknown",
                device_type: localStorage.getItem("device_type"),
            },
        });
        
        if (response.data && response.data.status === 403) {
            reportMyListRejected(type, token, response.status, response.data);
            return undefined;
        }

        return response.data;
    } catch (error: any) {
        console.error("Error fetching bookmarked content:", error);
        
        if (error.response && error.response.status === 403) {
            reportMyListRejected(type, token, 403, error.response.data);
            return undefined;
        }

        // errorMessage(error);
    }
};

export const saveUserPreferences = async (payload: any) => {
    try {
        const response = await api.post(
            `${BASE_URL}/user/tatva/content-preferences`,
            payload,
            userPreferencesApiConfig
        );
        return response.data;
    } catch (error) {
        console.error("Error saving user preferences:", error);
    }
};

export const getUserPreferences = async (userId: string) => {
    try {
        const response = await api.get(
            `${BASE_URL}/user/tatva/content-preferences?user_id=${userId}`,
            userPreferencesApiConfig
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching user preferences:", error);
    }
};

interface SolrSearchDetailParams {
    docIds: string[];
    start?: number;
    rows?: number;
}

export const getSolrContentDetail = async ({
                                               docIds,
                                               start = 0,
                                               rows = 5,
                                           }: SolrSearchDetailParams) => {
    try {
        // Create the OR condition for IDs
        const idCondition = docIds.map((id) => `${id}`).join(" OR ");

        // Create the boosting query with descending weights
        const boostQuery = docIds
            .map((id, index) => `id:${id}^${docIds.length - index}`)
            .join(" OR ");

        const response = await fetch(`${SOLR_BASE_URL}/select`, {
            method: "POST",
            headers: {
                authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
                accept: "application/json",
                "content-type": "application/json",
            },
            body: JSON.stringify({
                params: {
                    q: "*:*",
                    defType: "edismax",
                    qf: "id",
                    fl: "score,*",
                    fq: [`id:(${idCondition})`],
                    bq: boostQuery,
                    start: start,
                    rows: rows,
                },
            }),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch content detail");
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching SOLR content detail:", error);
        throw error;
    }
};

interface ContentItem {
    contentId: number;
    contentTypeId: number;
}

interface ContentCountsParams {
    contents: ContentItem[];
    token: string;
    userId: string;
}

export const getContentCounts = async ({
                                           contents,
                                           token,
                                           userId,
                                       }: ContentCountsParams) => {
    try {
        const payload = {
            user: userId,
            contents: contents.map((content) => ({
                contentTypeId: content.contentTypeId,
                contentId: content.contentId,
            })),
        };

        const response = await axios.post(
            `${BASE_URL}/user/tatva/content-counts-batch`,
            payload,
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    country: "1",
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching content counts:", error);
        errorMessage(error);
    }
};


export const getQuizDetail = async (contentInfoId: number, token: string) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/quiz/${contentInfoId}`,
            {}, {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching quiz detail:", error);
        errorMessage("Error fetching quiz details");
        // throw error;
    }
};

export const getQuizStats = async (contentInfoId: number, token: string) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/quiz/stats/${contentInfoId}`,
            {}, {
                headers: {
                    country: "1",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching quiz stats:", error);
        errorMessage("Error fetching quiz stats");
        // throw error;
    }
};

export const postQuizResponse = async (payload: any, token: string) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/quiz/submit`,
            payload, {
                headers: {
                    country: "1",
                    "Content-Type": "application/json",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error submitting quiz:", error);
        errorMessage("Error submitting quiz");
        // throw error;
    }
};

export const getQuizResult = async (quizId: any, userInfoId: any, token: string) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/quiz/response/${quizId}/${userInfoId}`,
            {}, {
                headers: {
                    country: "1",
                    "Content-Type": "application/json",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching quiz response:", error);
        errorMessage("Error fetching quiz response");
        // throw error;
    }
};

export const getAllQuiz = async (size: any, token: string) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/quiz/stats/all?size=${size}`,
            {}, {
                headers: {
                    country: "1",
                    "Content-Type": "application/json",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching quizzes:", error);
        errorMessage("Error fetching quizzes");
        // throw error;
    }
};

export const pediaLogout = async (token: string) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/user/user-logout`,
             {
                headers: {
                    country: "1",
                    "Content-Type": "application/json",
                    device_info: localStorage.getItem('device_info'),
                    device_lat: "unknown",
                    device_lng: "unknown",
                    device_location: "unknown",
                    device_type: localStorage.getItem('device_type'),
                    Authorization: `Bearer ${token}`,
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error logging out:", error);
        errorMessage("Error logging out");
        // throw error;
    }
};

export const storeSearchTerm = async (payload: any) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/tatva/content/frequent-search`,
      payload, // This should be just the payload data
      {
        headers: {
          api_key: USER_MANAGEMENT_API_KEY,
          api_secret_key: USER_MANAGEMENT_API_SECRET_KEY,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error storing search history:", error);
  }
};

export const getSearchHistory = async (b2c: string) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/tatva/content/frequent-search?b2c=${b2c}`,
      {
        headers: {
          api_key: USER_MANAGEMENT_API_KEY,
          api_secret_key: USER_MANAGEMENT_API_SECRET_KEY,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching search history:", error);
  }
};

interface GroupedContentParams {
  consolidatedModuleIds?: number[];
  specialityIds?: number[];
  page?: number;
  pageSize?: number;
}

export const getGroupedLatestBannerContent = async ({
  consolidatedModuleIds = [6001, 6002, 6003, 6004, 6005, 6006, 6007],
  specialityIds,
  page = 0,
  pageSize = 7,
}: GroupedContentParams = {}) => {
  try {
    
    const fq = [
      "status:489",
      "contentCountryId:(1)",
      `consolidatedModuleId:(${consolidatedModuleIds.join(" OR ")})`,
    ];

    // Add speciality filter if provided
    if (specialityIds && specialityIds.length > 0) {
      fq.push(`speciality_id:(${specialityIds.join(" OR ")})`);
    }

    const response = await fetch(`${SOLR_BASE_URL}/select`, {
      method: "POST",
      headers: {
        authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        params: {
          q: "*:*",
          fq,
          sort: "publishedDate desc",
          start: page * pageSize,
          rows: pageSize,
          group: true,
          "group.field": "consolidatedModuleId",
          "group.limit": 3,
          "group.sort": "publishedDate desc",
        },
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch grouped content");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching grouped content:", error);
    throw error;
  }
};

interface LatestContentParams {
  excludedIds?: string[];
  specialityIds?: number[];
  page?: number;
  pageSize?: number;
}

export const getLatest8Contents = async ({
  excludedIds,
  specialityIds,
  page = 0,
  pageSize = 8,
}: LatestContentParams = {}) => {
  try {
    
    const fq = [
      "status:489",
      "contentCountryId:(1)",
      `scheduled_published_date:[* TO ${new Date().toISOString()}]`,
    ];
    
    // Only add exclusion filter if there are excluded IDs
    if (excludedIds && excludedIds.length > 0) {
      fq.push(`-id:(${excludedIds.join(" ")})`);
    }

    // Add speciality filter if provided
    if (specialityIds && specialityIds.length > 0) {
      fq.push(`speciality_id:(${specialityIds.join(" OR ")})`);
    }

    const response = await fetch(`${SOLR_BASE_URL}/select`, {
      method: "POST",
      headers: {
        authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        params: {
          q: "*:*",
          fq,
          sort: "publishedDate desc",
          start: page * pageSize,
          rows: pageSize,
        },
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch latest content");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching latest content:", error);
    throw error;
  }
};

interface GroupedContentByFormatParams {
  consolidatedModuleId?: number;
  contentFormatIds?: number[];
  specialityIds?: number[];
  page?: number;
  pageSize?: number;
}

export const getGroupedContentByFormat = async ({
  consolidatedModuleId = 6002,
  contentFormatIds = [1000, 1001, 1002, 1003],
  specialityIds,
  page = 0,
  pageSize = 8,
}: GroupedContentByFormatParams = {}) => {
  try {
    const fq = [
      "status:489",
      "contentCountryId:(1)",
      `consolidatedModuleId:(${consolidatedModuleId})`,
      `contentFormatId:(${contentFormatIds.join(" OR ")})`,
    ];

    // Add speciality filter if provided
    if (specialityIds && specialityIds.length > 0) {
      fq.push(`speciality_id:(${specialityIds.join(" OR ")})`);
    }

    const response = await fetch(`${SOLR_BASE_URL}/select`, {
      method: "POST",
      headers: {
        authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        params: {
          q: "*:*",
          fq,
          sort: "publishedDate desc",
          start: page * pageSize,
          rows: pageSize,
          group: true,
          "group.field": "contentFormatId",
          "group.limit": 15,
          "group.sort": "publishedDate desc",
        },
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch grouped content by format");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching grouped content by format:", error);
    throw error;
  }
};

interface ContentByFormatParams {
  consolidatedModuleId?: number;
  contentFormatId?: number;
  specialityIds?: number[];
  page?: number;
  pageSize?: number;
}

export const getContentByFormat = async ({
  consolidatedModuleId = 6002,
  contentFormatId = 1001,
  specialityIds,
  page = 0,
  pageSize = 8,
}: ContentByFormatParams = {}) => {
  try {
    const fq = [
      "status:489",
      "contentCountryId:(1)",
      `consolidatedModuleId:(${consolidatedModuleId})`,
      `contentFormatId:(${contentFormatId})`,
    ];

    // Add speciality filter if provided
    if (specialityIds && specialityIds.length > 0) {
      fq.push(`speciality_id:(${specialityIds.join(" OR ")})`);
    }

    const response = await fetch(`${SOLR_BASE_URL}/select`, {
      method: "POST",
      headers: {
        authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        params: {
          q: "*:*",
          fq,
          sort: "publishedDate desc",
          start: page * pageSize,
          rows: pageSize,
        },
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch content by format");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching content by format:", error);
    throw error;
  }
};
